import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, ShoppingBag, Store, Settings, LogOut, CalendarOff, BookOpen, TrendingUp } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { Logo } from "@/components/lembra/Logo";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useProfile, useBakeryPartner } from "@/hooks/queries";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const navItems = [
  { title: "Dashboard", url: "/bakery/dashboard", icon: LayoutDashboard },
  { title: "Orders", url: "/bakery/orders", icon: ShoppingBag },
  { title: "Catalogue", url: "/bakery/catalogue", icon: BookOpen },
  { title: "Availability", url: "/bakery/availability", icon: CalendarOff },
  { title: "Performance", url: "/bakery/stats", icon: TrendingUp },
  { title: "My Profile", url: "/bakery/profile", icon: Store },
  { title: "Settings", url: "/bakery/settings", icon: Settings },
];

function BakerySidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();

  return (
    <Sidebar collapsible="icon" className="border-none">
      <div className="flex h-full flex-col bg-navy overflow-y-auto">
        {/* Logo + label */}
        <div className={`flex flex-col items-center py-6 ${collapsed ? "px-0" : "px-4"}`}>
          {!collapsed && (
            <div className="w-full">
              <Logo size="md" onDark />
              <p className="text-white/50 text-xs text-center mt-1.5 font-medium uppercase tracking-widest">
                Vendor Partner
              </p>
            </div>
          )}
          {collapsed && (
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-butter">
              <span className="text-navy font-bold text-sm">L</span>
            </div>
          )}
        </div>

        <SidebarContent className="bg-navy px-1">
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {navItems.map((item) => {
                  const isActive = location.pathname === item.url;
                  const linkContent = (
                    <NavLink
                      to={item.url}
                      end
                      className={`flex items-center rounded-lg text-sm font-medium transition-all duration-200 ${
                        collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2.5"
                      } ${
                        isActive
                          ? "bg-butter text-navy"
                          : "text-white/80 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <item.icon className="h-5 w-5 shrink-0" />
                      {!collapsed && <span>{item.title}</span>}
                    </NavLink>
                  );

                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild>
                        {collapsed ? (
                          <Tooltip>
                            <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                            <TooltipContent side="right" className="bg-navy text-white border-navy">
                              {item.title}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          linkContent
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </div>
    </Sidebar>
  );
}

function BakeryAvatarMenu() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const profileQuery = useProfile();
  const partnerQuery = useBakeryPartner();

  const displayName = profileQuery.data?.full_name || user?.email || "";
  const initials = displayName.charAt(0).toUpperCase() || "?";
  const bakeryName = partnerQuery.data?.vendor_name ?? "";

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/bakery-login");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 focus:outline-none">
          {bakeryName && <span className="text-sm font-medium text-navy hidden sm:block">{bakeryName}</span>}
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-coral text-white text-sm font-bold hover:bg-coral/90 transition-colors">
            {initials}
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem onClick={() => navigate("/bakery/settings")} className="cursor-pointer">
          <Settings className="mr-2 h-4 w-4" /> Settings
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-coral focus:text-coral">
          <LogOut className="mr-2 h-4 w-4" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function BakeryLayout() {
  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <BakerySidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 flex items-center justify-between border-b border-charcoal/10 px-4 bg-white sticky top-0 z-10">
            <SidebarTrigger />
            <BakeryAvatarMenu />
          </header>
          <main className="flex-1 bg-cream p-6 md:p-10">
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
